import {
  classifyGlyphGrab,
  createGlyphPlacements,
  fontDeclaration,
  layoutGlyphs,
  type GlyphInteractionMode,
  type GlyphPlacement,
  type NormalizedPoint,
} from "./layout";

export interface ActiveGlyphInteraction {
  glyphId: number;
  mode: GlyphInteractionMode;
}

export class TextOverlayRenderer {
  private readonly context: CanvasRenderingContext2D;
  private cssWidth = 1;
  private cssHeight = 1;
  private scale = 1;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) {
      throw new Error("Canvas 2D is required for sharp text rendering.");
    }
    this.context = context;
  }

  resize(cssWidth: number, cssHeight: number): void {
    this.cssWidth = Math.max(1, cssWidth);
    this.cssHeight = Math.max(1, cssHeight);
    this.scale = Math.min(window.devicePixelRatio || 1, 2.5);
    this.canvas.width = Math.round(this.cssWidth * this.scale);
    this.canvas.height = Math.round(this.cssHeight * this.scale);
    this.canvas.style.width = `${this.cssWidth}px`;
    this.canvas.style.height = `${this.cssHeight}px`;
    this.context.setTransform(this.scale, 0, 0, this.scale, 0, 0);
  }

  createPlacements(
    word: string,
    center: NormalizedPoint,
  ): GlyphPlacement[] {
    return createGlyphPlacements(
      this.context,
      word,
      this.cssWidth,
      this.cssHeight,
      center,
    );
  }

  classifyGrab(
    word: string,
    placements: readonly GlyphPlacement[],
    glyphId: number,
    point: NormalizedPoint,
  ): GlyphInteractionMode {
    const layout = layoutGlyphs(
      this.context,
      word,
      placements,
      this.cssWidth,
      this.cssHeight,
    ).find((candidate) => candidate.placement.id === glyphId);
    if (!layout) {
      return "translate";
    }
    return classifyGlyphGrab(layout, {
      x: point.x * this.cssWidth,
      y: point.y * this.cssHeight,
    });
  }

  draw(
    word: string,
    placements: readonly GlyphPlacement[],
    activeInteraction: ActiveGlyphInteraction | null = null,
  ): void {
    this.context.setTransform(this.scale, 0, 0, this.scale, 0, 0);
    this.context.clearRect(0, 0, this.cssWidth, this.cssHeight);
    const layouts = layoutGlyphs(
      this.context,
      word,
      placements,
      this.cssWidth,
      this.cssHeight,
    );

    const activeGlyphId = activeInteraction?.glyphId ?? null;
    const orderedLayouts =
      activeGlyphId === null
        ? layouts
        : [
            ...layouts.filter(
              (layout) => layout.placement.id !== activeGlyphId,
            ),
            ...layouts.filter(
              (layout) => layout.placement.id === activeGlyphId,
            ),
          ];

    for (const layout of orderedLayouts) {
      const dragging = layout.placement.id === activeGlyphId;
      this.context.font = fontDeclaration(layout.fontSize);
      this.context.textAlign = "center";
      this.context.textBaseline = "alphabetic";
      this.context.lineJoin = "round";

      if (dragging && activeInteraction?.mode === "rotate") {
        this.drawRotationGuide(layout);
      }

      const glow = Math.max(7, layout.fontSize * 0.035);
      this.context.shadowColor = dragging
        ? "rgba(210, 240, 255, 0.42)"
        : "rgba(160, 210, 255, 0.25)";
      this.context.shadowBlur = glow;
      this.context.fillStyle = dragging
        ? "rgba(248, 250, 246, 1)"
        : "rgba(235, 238, 232, 1)";
      this.context.save();
      this.context.translate(layout.centerX, layout.centerY);
      this.context.rotate(layout.placement.rotation);
      this.context.fillText(
        layout.placement.character,
        0,
        layout.baseline - layout.centerY,
      );

      this.context.shadowBlur = 0;
      this.context.lineWidth = Math.max(1, layout.fontSize * 0.006);
      this.context.strokeStyle = "rgba(255, 255, 255, 0.7)";
      this.context.strokeText(
        layout.placement.character,
        0,
        layout.baseline - layout.centerY,
      );
      this.context.restore();
    }
  }

  private drawRotationGuide(
    layout: ReturnType<typeof layoutGlyphs>[number],
  ): void {
    this.context.save();
    this.context.translate(layout.centerX, layout.centerY);
    this.context.rotate(layout.placement.rotation);
    this.context.shadowBlur = 0;
    this.context.setLineDash([5, 7]);
    this.context.lineWidth = 1;
    this.context.strokeStyle = "rgba(205, 238, 255, 0.62)";
    this.context.beginPath();
    const radius = Math.max(layout.width, layout.height) * 0.68;
    this.context.arc(0, 0, radius, 0, Math.PI * 2);
    this.context.stroke();
    this.context.setLineDash([]);
    this.context.restore();
  }
}
